import FeatureTemplate from "../../components/FeatureTemplate";

const Helpdesk = () => {
  return (
    <FeatureTemplate 
      title="Helpdesk" 
      description="Centralized HR support with intelligent ticketing, SLA tracking, and automation"
      featureId="helpdesk"
    >
      <p className="mb-6">
        Our comprehensive Helpdesk system revolutionizes employee support with a centralized, intelligent platform that handles HR, IT, and administrative queries efficiently. The system combines automated workflows with human expertise to ensure rapid resolution of employee issues while maintaining complete visibility and accountability.
      </p>
      
      <p className="mb-6">
        Employees can raise tickets through multiple channels including web portal, mobile app, email, and our AI-powered chatbot. Each ticket is automatically categorized, prioritized, and assigned to the appropriate team based on issue type, department, and urgency, eliminating manual routing and reducing response times.
      </p>
      
      <p className="mb-6">
        Our SLA-driven escalation system ensures that every ticket receives timely attention, with automated reminders and escalation protocols for overdue issues. Managers gain real-time insights into resolution times, team performance, and employee satisfaction through comprehensive analytics dashboards.
      </p>
      
      <div className="bg-blue-50 p-6 rounded-xl mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Intelligent Support System</h3>
        <ul className="space-y-4">
          <li className="flex items-start space-x-3">
            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
            <div>
              <strong>Multi-Channel Intake:</strong> Seamless ticket creation via web portal, mobile app, email, chatbot, and voice assistants
            </div>
          </li>
          <li className="flex items-start space-x-3">
            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
            <div>
              <strong>Smart Ticket Routing:</strong> AI-powered categorization and assignment based on issue type, department, and agent expertise
            </div>
          </li>
          <li className="flex items-start space-x-3">
            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
            <div>
              <strong>SLA Management:</strong> Customizable service level agreements with automated escalation for overdue tickets
            </div>
          </li>
          <li className="flex items-start space-x-3">
            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
            <div>
              <strong>Knowledge Base Integration:</strong> AI suggests relevant articles during ticket creation and resolution
            </div>
          </li>
          <li className="flex items-start space-x-3">
            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
            <div>
              <strong>Automated Resolution:</strong> Chatbot resolves common queries instantly without human intervention
            </div>
          </li>
        </ul>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gray-50 p-6 rounded-xl">
          <h4 className="font-semibold text-gray-900 mb-3">Key Benefits</h4>
          <ul className="space-y-2">
            <li className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Reduces average resolution time by 65%</span>
            </li>
            <li className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Increases first-contact resolution by 80%</span>
            </li>
            <li className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Improves employee satisfaction by 75%</span>
            </li>
            <li className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Reduces support team workload by 50%</span>
            </li>
          </ul>
        </div>
        
        <div className="bg-gray-50 p-6 rounded-xl">
          <h4 className="font-semibold text-gray-900 mb-3">Analytics & Reporting</h4>
          <ul className="space-y-2">
            <li className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span>Real-time dashboards for team performance</span>
            </li>
            <li className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span>CSAT surveys and sentiment analysis</span>
            </li>
            <li className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span>Trend analysis for common issues</span>
            </li>
            <li className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span>SLA compliance reporting</span>
            </li>
          </ul>
        </div>
      </div>
      
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-xl mb-8">
        <h4 className="font-semibold text-lg mb-3">Intelligent Automation</h4>
        <p className="mb-4">
          Our AI-powered automation resolves up to 70% of routine queries without human intervention. The system learns from successful resolutions to continuously improve its knowledge base and response accuracy, while seamlessly escalating complex issues to human agents with complete context.
        </p>
        <div className="grid md:grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold">70%</div>
            <div className="text-sm opacity-90">Auto-Resolution</div>
          </div>
          <div>
            <div className="text-2xl font-bold">24/7</div>
            <div className="text-sm opacity-90">Availability</div>
          </div>
          <div>
            <div className="text-2xl font-bold">95%</div>
            <div className="text-sm opacity-90">SLA Compliance</div>
          </div>
        </div>
      </div>
      
      <p>
        Trusted by over 200 organizations across diverse industries, our Helpdesk system processes more than 1 million support tickets annually with 95% SLA compliance. The platform not only improves operational efficiency but also transforms employee support from a cost center into a strategic asset. With comprehensive analytics, intelligent automation, and seamless integration with our AI chatbot and knowledge base, our solution ensures that every employee query receives the attention it deserves while providing HR leaders with actionable insights to improve workplace experience and reduce recurring issues.
      </p>
    </FeatureTemplate>
  );
};

export default Helpdesk;